import React, { useState, useEffect } from 'react'
import { useStyle } from "./Style";
import Grid from "@material-ui/core/Grid";
import { SelectValidator, TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import MenuItem from '@mui/material/MenuItem';
import { InputAdornment } from '@mui/material';
import { getLocalNumberLength, getPartner } from '../../../../../lib/access.es6';
import { RELATIONSHIP_POOL } from "../../../../../containers/NewProductList/PoductConstants";
import _ from "lodash";
import { getPartnerGuid } from "../../../../../lib/access.es6";
import {
  nationalPhoneNumber,
  toStoredPhoneNumber,
  phoneDigits
} from "../../../../../lib/phoneNumbers.es6";
import { getFullName } from "../../../../../lib/personDetails.es6";
import {
  claimantMatchesDeceased,
  claimantIsDeceasedMessage
} from "../../../../../lib/claimantDeceasedGuard.es6";

const ClaimLoggingDetails = (props) => {
  const classes = useStyle();

  const [name, setName] = useState(props.claimState.claimant_full_name || "");
  const [phoneNumber, setPhoneNumber] = useState(props.claimState.claimant_phone_number || "");
  const [idNumber, setIdNumber] = useState(props.claimState.claimant_id_number || "");
  const [relationship, setRelationship] = useState(props.claimState.claimant_relationship_to_patient || null);
  const [member, setMember] = useState("");
  const [email, setEmail] = useState(props.claimState.claimant_email || '');
  const [paymentOption, setPaymentOption] = useState(props.claimState.claimant_payment_option || '');
  const [bankName, setBankName] = useState(props.claimState.claimant_bank_name || '');
  const [accountNumber, setAccountNumber] = useState(props.claimState.claimant_bank_account_number || '');
  const [mpesaNumber, setMpesaNumber] = useState(props.claimState.claimant_mpesa_phone_number || '');
  const [claimant_bank_account_holder, setClaimantBankAccountHolder] = useState(props.claimState.claimant_bank_account_holder || '');
  const [claimant_bank_branch_code, setClaimantBankBranchCode] = useState(props.claimState.claimant_bank_branch_code || '');
  const [claimantMatchesDeceasedError, setClaimantMatchesDeceasedError] = useState('');
  // Whether that message is a refusal or a "check this" — drives its colour only;
  // the gate itself reads claimant_is_deceased on the wrapper.
  const [claimantMatchIsBlocking, setClaimantMatchIsBlocking] = useState(false);

  const partnerGuid = getPartnerGuid();
  const OldMutualKenyaPaymentOptions = ["Bank", "Mpesa"];
  const OldMutualFLPPaymentOptions = ["Bank"];

  const members = props.eventQuote.policy_schedule_entries
    ? props.eventQuote.policy_schedule_entries
    : [];

  // The deceased is known from step 1. Excluding them from the claimant list is not a
  // Malawi-specific nicety — nobody can claim for their own death, and the customer
  // journey already blocks it, so the same rule applies for OMK (retest item 21).
  const excludeDeceased =
    partnerGuid === 'old_mutual_malawi' || partnerGuid === 'omk';
  const deceasedDependant = props.claimInitiationPayload.member
    ? props.claimInitiationPayload.member.dependant
    : null;

  const claimantDropdownMembers = (excludeDeceased && deceasedDependant)
    ? members.filter((entry) => entry.dependant.guid !== deceasedDependant.guid)
    : members;

  /**
   * Who can be selected as the claimant: the covered lives, plus the policy's
   * beneficiary / next of kin.
   *
   * The beneficiary was missing, which is precisely the case the client raised: when the
   * policyholder is the one who died, the beneficiary is the person who files the claim
   * (retest item 21). Each option carries only the fields it actually has — relationship
   * is left for the user, because on this form it means "relationship to the deceased",
   * not the role on the policy.
   */
  const beneficiaries = Array.isArray(props.beneficiaries)
    ? props.beneficiaries
    : [];
  const claimantOptions = beneficiaries
    .filter((b) => !!b && !!getFullName(b))
    .map((b, index) => ({
      key: 'beneficiary-' + index,
      label:
        getFullName(b) +
        ' — Beneficiary' +
        (b.relationship ? ' (' + b.relationship + ')' : ''),
      fullName: getFullName(b),
      idNumber: b.national_id || '',
      phone: b.msisdn || b.phone_number || '',
      email: b.email || '',
      relationship: ''
    }))
    .concat(
      claimantDropdownMembers.map((entry, index) => ({
        key: 'member-' + index,
        label: getFullName(entry.dependant),
        fullName: getFullName(entry.dependant),
        idNumber: entry.dependant.national_id || '',
        phone: entry.dependant.msisdn || entry.dependant.phone_number || '',
        email: entry.dependant.email || '',
        // Principal → "Self" only relative to the policy; relative to the deceased it
        // has to be stated, so it is left blank for the user to choose.
        relationship: ''
      }))
    );

  const runDeceasedMatchCheck = (currentName, currentIdNumber, currentPhone) => {
    if (!excludeDeceased) return;
    const result = claimantMatchesDeceased(deceasedDependant, {
      fullName: currentName,
      idNumber: currentIdNumber,
      phone: currentPhone
    });
    setClaimantMatchesDeceasedError(
      result.match ? claimantIsDeceasedMessage(result.on, result.blocking) : ''
    );
    setClaimantMatchIsBlocking(result.blocking);
    // Only a match on something that identifies a PERSON stops the claim. A phone
    // number is shown and explained but does not block: the field is auto-filled
    // from the member record, so on a policy whose dependants share the household
    // number, selecting any claimant would otherwise strand the claim entirely.
    props.setAdditionalState('claimant_is_deceased', result.blocking);
  };

  useEffect(() => {
    // Retest item 13: a hard "exactly N digits" rule rejected international claimant
    // numbers, and rejected local ones that the old prefix-slicing had mangled. The
    // field is now free-form within sane bounds: local numbers still pass at their
    // usual length, and an overseas next of kin can be paid.
    ValidatorForm.addValidationRule("isValidPhoneNumber", (value) => {
      if (!value) {
        return false;
      }
      const digits = phoneDigits(value);
      if (partnerGuid === 'omk') {
        return digits.length >= 9 && digits.length <= 15;
      }
      return value.length === getLocalNumberLength();
    });

    return function cleanup() {
      ValidatorForm.removeValidationRule("isValidPhoneNumber");
    };
  }, []);

  useEffect(() => {
    if (excludeDeceased) {
      runDeceasedMatchCheck(
        props.claimState.claimant_full_name || '',
        props.claimState.claimant_id_number || '',
        props.claimState.claimant_phone_number || ''
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const internationalCallingCode = getPartner().country
    .international_calling_code;

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12}>
          <div className={classes.headingclaim}>
            <h4>Claimant details?</h4>
          </div>
        </Grid>
      </Grid>
      <ValidatorForm onSubmit={() => { }}>
        <Grid container spacing={2} className={classes.selectpolicyWrapper}>
          <Grid item xs={12}>
            <SelectValidator
              labelId="select-member"
              id="select-member"
              value={member}
              name="selectmember"
              type="text"
              label="Select from policy member"
              onChange={(e) => {
                const picked = e.target.value;
                setMember(picked);

                const n = picked.fullName;
                const i = picked.idNumber;
                // The stored number is not one shape: +254798676788 from one journey,
                // 00254729874002 from another. Slicing a fixed five characters off the
                // front produced an 8-digit number from the former, which then failed
                // this form's own phone check (retest item 13).
                const local = nationalPhoneNumber(picked.phone);

                setName(n);
                setIdNumber(i);
                setPhoneNumber(local);
                props.setAdditionalState("claimant_full_name", n);
                props.setAdditionalState("claimant_id_number", i);
                props.setAdditionalState(
                  "claimant_phone_number",
                  local ? toStoredPhoneNumber(local) : ""
                );
                if (picked.email) {
                  setEmail(picked.email);
                  props.setAdditionalState("claimant_email", picked.email);
                }
                if (picked.relationship) {
                  setRelationship(picked.relationship);
                  props.setAdditionalState(
                    "claimant_relationship_to_patient",
                    picked.relationship
                  );
                }
                runDeceasedMatchCheck(n, i, local);
              }}
              variant="outlined"
              validators={["required"]}
              errorMessages={["Please select a member"]}
              style={{ width: "100%" }}
              sx={{
                fontFamily: "Open Sans",
                fontStyle: "normal",
                fontWeight: 300,
                fontSize: "12px",
                lineHeight: "16px",
                letterSpacing: "0.005em",
                color: "#000000",
              }}
            >
              {claimantOptions.map((option) => (
                <MenuItem
                  key={option.key}
                  sx={{
                    fontFamily: "Open Sans",
                    fontStyle: "normal",
                    fontWeight: 300,
                    fontSize: "12px",
                    lineHeight: "16px",
                    letterSpacing: "0.005em",
                    color: "#000000",
                  }}
                  value={option}
                >
                  {option.label}
                </MenuItem>
              ))}
            </SelectValidator>
            <hr />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <TextValidator
              id="name"
              style={{ width: "100%" }}
              label="Full Names"
              name="name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                props.setAdditionalState("claimant_full_name", e.target.value);
              }}
              onBlur={() => runDeceasedMatchCheck(name, idNumber, phoneNumber)}
              variant="outlined"
              validators={["required"]}
              errorMessages={["Please enter you full names"]}
            />
          </Grid>
          {excludeDeceased && claimantMatchesDeceasedError && (
            <Grid item xs={12}>
              {/* Red for a match that stops the claim, amber for one that is only
                  worth checking, so the two do not read as the same severity. */}
              <p
                style={{
                  color: claimantMatchIsBlocking ? '#8F191C' : '#8a5300',
                  fontSize: '12px',
                  margin: '0 8px'
                }}
              >
                {claimantMatchesDeceasedError}
              </p>
            </Grid>
          )}
          <Grid item xs={12} md={6} sm={6}>
            <TextValidator
              id="phone"
              style={{ width: "100%" }}
              label="Phone"
              name="phone"
              value={phoneNumber}
              onChange={(e) => {
                setPhoneNumber(e.target.value);
                props.setAdditionalState(
                  "claimant_phone_number",
                  e.target.value ? toStoredPhoneNumber(e.target.value) : ""
                );
              }}
              onBlur={() => runDeceasedMatchCheck(name, idNumber, phoneNumber)}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    sx={{
                      fontSize: "small",
                      color: "#000",
                      background: "#F7F7F7",
                    }}
                    position="start"
                  >
                    {" "}
                    <span
                      style={{
                        fontSize: "small",
                        color: "#000",
                      }}
                    >
                      {/* The partner country record is returning 0027 for OMK,
                          which was rendered verbatim next to a Kenyan number
                          (retest item 13). A plain + is correct whatever the
                          country, and the code is now normalised on write. */}
                      {partnerGuid === "omk"
                        ? "+"
                        : internationalCallingCode
                          ? internationalCallingCode
                          : ""}
                    </span>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              validators={["isValidPhoneNumber"]}
              errorMessages={[
                `Please enter a valid ${getLocalNumberLength()} digit phone number`,
              ]}
            />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <TextValidator
              id="id"
              style={{ width: "100%" }}
              label="Id number"
              name="id"
              value={idNumber}
              onChange={(e) => {
                setIdNumber(e.target.value);
                props.setAdditionalState("claimant_id_number", e.target.value);
              }}
              onBlur={() => runDeceasedMatchCheck(name, idNumber, phoneNumber)}
              variant="outlined"
              validators={["required"]}
              errorMessages={["Please enter you id number"]}
            />
          </Grid>
          {(partnerGuid === "omk" || partnerGuid === "omu_flp" || partnerGuid === "old_mutual_malawi") && (
            <Grid item xs={12} md={6} sm={6}>
              <TextValidator
                id="email"
                style={{ width: "100%" }}
                label="Email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  props.setAdditionalState("claimant_email", e.target.value);
                }}
                variant="outlined"
                validators={["required"]}
                errorMessages={["Please enter you claimant email"]}
              />
            </Grid>
          )}
          <Grid item xs={12} md={6} sm={6}>
            <SelectValidator
              labelId="select-relationship"
              id="select-relationship"
              value={relationship}
              label="Relationship"
              name="relationships"
              onChange={(event) => {
                setRelationship(event.target.value);
                props.setAdditionalState(
                  "claimant_relationship_to_patient",
                  event.target.value
                );
              }}
              style={{ width: "100%" }}
              sx={{
                fontFamily: "Open Sans",
                fontStyle: "normal",
                fontWeight: 300,
                fontSize: "12px",
                lineHeight: "16px",
                letterSpacing: "0.005em",
                color: "#000000",
              }}
              variant="outlined"
              InputLabelProps={{
                shrink: !!relationship,
              }}
            >
              {Object.entries(RELATIONSHIP_POOL)
                .sort(([, vA], [, vB]) => vA.localeCompare(vB))
                .map(([key, value]) => {
                  return (
                    <MenuItem
                      key={value}
                      sx={{
                        fontFamily: "Open Sans",
                        fontStyle: "normal",
                        fontWeight: 300,
                        fontSize: "12px",
                        lineHeight: "16px",
                        letterSpacing: "0.005em",
                        color: "#000000",
                      }}
                      //disabled={principalAdded}
                      value={key}
                    >
                      {_.startCase(key)}
                    </MenuItem>
                  );
                })}
            </SelectValidator>
          </Grid>
          {(partnerGuid === "omk" || partnerGuid === "omu_flp") && (
            <Grid item xs={12} sm={6} md={6}>
              <SelectValidator
                labelId="payment-option"
                id="payment-option"
                value={paymentOption}
                name="paymentOption"
                type="text"
                label="Select Payment Option"
                onChange={(e) => {
                  setPaymentOption(e.target.value);
                  props.setAdditionalState(
                    "claimant_payment_option",
                    e.target.value
                  );
                }}
                variant="outlined"
                validators={["required"]}
                errorMessages={["Please select a payment option"]}
                style={{ width: "100%" }}
                sx={{
                  fontFamily: "Open Sans",
                  fontStyle: "normal",
                  fontWeight: 300,
                  fontSize: "12px",
                  lineHeight: "16px",
                  letterSpacing: "0.005em",
                  color: "#000000",
                }}
              >
                {(partnerGuid === "omk" && OldMutualKenyaPaymentOptions ? OldMutualKenyaPaymentOptions : partnerGuid === "omu_flp" && OldMutualFLPPaymentOptions).map((option, index) => {
                  return (
                    <MenuItem
                      key={index}
                      sx={{
                        fontFamily: "Open Sans",
                        fontStyle: "normal",
                        fontWeight: 300,
                        fontSize: "12px",
                        lineHeight: "16px",
                        letterSpacing: "0.005em",
                        color: "#000000",
                      }}
                      value={option}
                    >
                      {option}
                    </MenuItem>
                  );
                })}
              </SelectValidator>
            </Grid>
          )}
          {paymentOption === "Bank" && (
            <Grid item xs={12} md={6} sm={6}>
              <TextValidator
                id="bank-name"
                style={{ width: "100%" }}
                label="Bank Name"
                name="bankName"
                value={bankName}
                onChange={(e) => {
                  setBankName(e.target.value);
                  props.setAdditionalState(
                    "claimant_bank_name",
                    e.target.value
                  );
                }}
                variant="outlined"
                validators={["required"]}
                errorMessages={["Please enter a bank name"]}
              />
            </Grid>
          )}
          {paymentOption === "Bank" && (
            <Grid item xs={12} md={6} sm={6}>
              <TextValidator
                id="account-number"
                style={{ width: "100%" }}
                label="Account Number"
                name="accountNumber"
                value={accountNumber}
                onChange={(e) => {
                  setAccountNumber(e.target.value);
                  props.setAdditionalState(
                    "claimant_bank_account_number",
                    e.target.value
                  );
                }}
                variant="outlined"
                validators={["required"]}
                errorMessages={["Please enter a valid account number"]}
              />
            </Grid>
          )}

          {
            paymentOption === "Bank" && (
              <Grid item xs={12} md={6} sm={6}>
                <TextValidator
                  id="account-holder"
                  style={{ width: "100%" }}
                  label="Account Holder Name"
                  name="accountHolder"
                  value={claimant_bank_account_holder}
                  onChange={(e) => {
                    setClaimantBankAccountHolder(e.target.value);
                    props.setAdditionalState(
                      "claimant_bank_account_holder",
                      e.target.value
                    );
                  }}
                  variant="outlined"
                  validators={["required"]}
                  errorMessages={["Please enter the account holder's name"]}
                />
              </Grid>
            )
          }

          {
            paymentOption === "Bank" && (
              <Grid item xs={12} md={6} sm={6}>
                <TextValidator
                  id="bank-branch-code"
                  style={{ width: "100%" }}
                  label="Bank Branch Code"
                  name="bankBranchCode"
                  value={claimant_bank_branch_code}
                  onChange={(e) => {
                    setClaimantBankBranchCode(e.target.value);
                    props.setAdditionalState(
                      "claimant_bank_branch_code",
                      e.target.value
                    );
                  }}
                  variant="outlined"
                  validators={["required"]}
                  errorMessages={["Please enter the bank branch code"]}
                />
              </Grid>
            )
          }

          {paymentOption === "Mpesa" && (
            <Grid item xs={12} md={6} sm={6}>
              <TextValidator
                id="mpesa-number"
                style={{ width: "100%" }}
                label="Mpesa Number"
                name="mpesaNumber"
                value={mpesaNumber}
                onChange={(e) => {
                  setMpesaNumber(e.target.value);
                  props.setAdditionalState(
                    "claimant_mpesa_phone_number",
                    e.target.value
                  );
                }}
                variant="outlined"
                validators={["required"]}
                errorMessages={["Please enter a valid mpesa number"]}
              />
            </Grid>
          )}
        </Grid>
      </ValidatorForm>
    </>
  );
};

export default ClaimLoggingDetails